import { decodeTime } from 'ulid';

import { buildProfileSnapshot, entityTargetsFromMessageData } from '../../lib/entitySnapshotBuilders';

import { messageIdFromString, sortOrderFromString } from '../core/chatItem';
import { textFromSanitizedHtml } from '../core/htmlText';
import type { FeedParseError, FeedParseResult } from '../core/parsePrimitives';
import type {
  AuthorId,
  ChannelId,
  ChatPreview,
  ChatPreviewImage,
  ClientRequestId,
  EmojiId,
  EntityBundle,
  FeedPage,
  LiveWireReaction,
  LocalOptimisticMessage,
  MessageId,
  SortOrder,
  SyncCursor,
  WireAttachment,
  WireMediaVariant,
  WireMessage,
  WorkspaceId,
} from '../core/types';
import { ECHO_INVALID_MESSAGE_FLAG } from './invalidMessage';
import { cloneEchoRendererRow } from './rendererRow';

/** Re-exported branded id/sort-order constructors for tests and echo payload builders. */
export { messageIdFromString, sortOrderFromString } from '../core/chatItem';

/** Echo message type tags that affect rendering but are not core feed discriminants. */
export type EchoMessageType = 'announcement' | 'conference_transcript' | 'widget_announcement' | 'end_of_messages';

/** Echo author discriminator; null on terminal/system rows before fallback author projection. */
export type EchoAuthorType = 'profile' | 'shadow';

/** Echo mutation action values carried by sync and live event payloads. */
export type EchoWireAction = 'create' | 'update' | 'destroy';

/** Attachment payload after parser normalization; thumbnail geometry is optional metadata. */
export interface EchoWireAttachment extends WireAttachment {
  readonly workspaceId?: WorkspaceId;
  readonly thumbWidth?: number;
  readonly thumbHeight?: number;
  readonly contentMd5?: string;
  readonly createdAt: number;
  readonly updatedAt: number;
}

/** Reaction payload after parser normalization; sticker transform fields are nullable numbers. */
export interface EchoWireReaction extends LiveWireReaction {
  readonly workspaceId: WorkspaceId;
  readonly channelId: ChannelId;
  readonly messageId: MessageId;
  readonly posX: number | null;
  readonly posY: number | null;
  readonly scale: number | null;
  readonly angle: number | null;
  readonly clientRequestId?: ClientRequestId;
}

/** Mention payload from echo; timestamp is Unix seconds and source fields are present by backend feature. */
export interface EchoWireMention {
  readonly id: string;
  readonly workspaceId: WorkspaceId;
  readonly timestamp: number;
  readonly senderId: AuthorId;
  readonly recipientId: AuthorId;
  readonly sourceType?: string;
  readonly sourceId?: string;
  readonly mentionableType?: string;
  readonly mentionableId?: string;
  readonly emailSubject?: string;
  readonly notificationTitle?: string;
  readonly summary?: string;
}

/** Raw echo link-preview payload; image/logo geometry is present only when the URL has that asset. */
export interface EchoWireLinkPreview {
  readonly id: string;
  readonly dismissed: boolean;
  readonly dismissedAt?: string;
  readonly url: string;
  readonly title?: string;
  readonly description?: string;
  readonly imageUrl?: string;
  readonly imageWidth?: number;
  readonly imageHeight?: number;
  readonly logoUrl?: string;
  readonly logoWidth?: number;
  readonly logoHeight?: number;
  readonly publisher?: string;
  readonly author?: string;
  readonly lang?: string;
  readonly canonicalUrl?: string;
  readonly date?: number;
  readonly datePublished?: number;
  readonly dateModified?: number;
}

/** Core chat preview plus echo ownership/dismissal metadata preserved for later UI work. */
export interface EchoChatPreview extends ChatPreview {
  readonly id: string;
  readonly dismissed: boolean;
  readonly dismissedAt?: string;
  readonly imageUrl?: string;
  readonly imageWidth?: number;
  readonly imageHeight?: number;
  readonly logoUrl?: string;
  readonly logoWidth?: number;
  readonly logoHeight?: number;
  readonly publisher?: string;
  readonly author?: string;
  readonly lang?: string;
  readonly canonicalUrl?: string;
  readonly date?: number;
  readonly datePublished?: number;
  readonly dateModified?: number;
}

/** Parsed echo message with sanitized body text, original HTML, and raw echo collections retained. */
export interface EchoWireMessage extends Omit<WireMessage, 'attachments' | 'previews' | 'reactions'> {
  readonly rawAuthorId: AuthorId | null;
  readonly authorType: EchoAuthorType | null;
  readonly type?: EchoMessageType;
  /** Typed-row payload (announcement mode, widget refs, …); must round-trip durable storage. */
  readonly data?: Record<string, unknown>;
  /** Widget-announcement serializer extras; must also round-trip durable storage. */
  readonly actors?: Record<string, unknown>[];
  readonly sourceWidgetId?: string;
  readonly sourceWidget?: Record<string, unknown>;
  readonly clientRequestId?: ClientRequestId;
  readonly replyToMessageId?: MessageId;
  readonly mentions: EchoWireMention[];
  readonly linkPreviews: EchoWireLinkPreview[];
  readonly attachments: EchoWireAttachment[];
  readonly previews: EchoChatPreview[];
  readonly reactions: EchoWireReaction[];
}

/** Feed page success data with optional cursors omitted when echo sends terminal nulls. */
export interface EchoFeedPage extends FeedPage {
  readonly messages: EchoWireMessage[];
  readonly rendererProfiles: EchoRendererProfile[];
  readonly resolvedSortOrder?: SortOrder;
  readonly invalidMessages?: EchoInvalidMessageIssue[];
}

/** Parse issue retained without the malformed payload for diagnostics at the API boundary. */
export interface EchoInvalidMessageIssue extends FeedParseError {
  readonly id?: MessageId;
  readonly sortOrder?: SortOrder;
  readonly disposition: 'placeholder' | 'skipped';
}

/** Sync event payload; destroy events carry null message and must be resolved from cached sort order. */
interface EchoWireSyncEventBase {
  readonly cursor: SyncCursor;
  readonly sortOrder: SortOrder;
}

export type EchoWireSyncEvent = EchoWireSyncEventBase &
  (
    | { readonly action: 'destroy'; readonly message: null }
    | { readonly action: Exclude<EchoWireAction, 'destroy'>; readonly message: EchoWireMessage }
  );

/** Sync response data with overflow flag and the next cursor the caller should persist. */
export interface EchoWireSyncResponse {
  readonly events: EchoWireSyncEvent[];
  readonly rendererProfiles: EchoRendererProfile[];
  readonly overflow: boolean;
  readonly nextCursor: SyncCursor;
}

/** Full profile row bundled for Dexie persistence and immediate legacy renderer decoration. */
export interface EchoRendererProfile extends Record<string, unknown> {
  readonly id: AuthorId;
  readonly workspace_id: WorkspaceId;
  readonly full_name: string;
  readonly flexicon: Record<string, unknown>;
  readonly role: string;
  readonly tag: string | null;
  readonly tag_color: string | null;
}

/** Send response data containing the authoritative server-created message. */
export interface EchoSendResponse {
  readonly message: EchoWireMessage;
}

/** Live message event detail; cursor is absent on local/fallback dispatches and then sortOrder is used. */
export interface EchoSocketMessageEventDetail {
  readonly action: EchoWireAction;
  readonly cursor?: SyncCursor;
  readonly message: EchoWireMessage;
}

/** Live reaction event detail parsed separately from core message sync events. */
export interface EchoSocketReactionEventDetail {
  readonly action: EchoWireAction;
  readonly reaction: EchoWireReaction;
}

/** Parsed locally-dispatched optimistic message detail. */
export interface EchoLocalOptimisticMessageEventDetail {
  readonly action: 'create';
  readonly message: LocalOptimisticMessage;
}

/** Echo error envelope parsed before success payloads so API errors throw through EchoFeedApiError. */
export interface EchoErrorEnvelope {
  readonly ok: false;
  readonly errors: string[];
  readonly validations?: Record<string, string[]>;
}

/** Parses one echo message, normalizing snake_case, Unix seconds, HTML text, and nested collections. */
export function parseEchoWireMessage(value: unknown, path = '$'): FeedParseResult<EchoWireMessage> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const id = readRequired(object.value, 'id', path, parseMessageId);
  if (!id.ok) return id;
  const workspaceId = readRequired(object.value, 'workspace_id', path, parseWorkspaceId);
  if (!workspaceId.ok) return workspaceId;
  const channelId = readRequired(object.value, 'channel_id', path, parseChannelId);
  if (!channelId.ok) return channelId;
  const text = readRequiredNullable(object.value, 'text', path, parseString);
  if (!text.ok) return text;
  const normalizedText = text.value ?? '';
  const sortOrder = readRequired(object.value, 'sort_order', path, parseSortOrder);
  if (!sortOrder.ok) return sortOrder;
  const createdAt = readRequired(object.value, 'timestamp', path, parseUnixSecondsToIso);
  if (!createdAt.ok) return createdAt;
  const rawAuthorId = readRequiredNullable(object.value, 'author_id', path, parseAuthorId);
  if (!rawAuthorId.ok) return rawAuthorId;
  const authorType = readRequiredNullable(object.value, 'author_type', path, parseAuthorType);
  if (!authorType.ok) return authorType;
  const attachments = readRequired(object.value, 'attachments', path, parseEchoWireAttachments);
  if (!attachments.ok) return attachments;
  const reactions = readRequired(object.value, 'reactions', path, parseEchoWireReactions);
  if (!reactions.ok) return reactions;
  const mentions = readRequired(object.value, 'mentions', path, parseEchoWireMentions);
  if (!mentions.ok) return mentions;
  const linkPreviews = readRequired(object.value, 'link_previews', path, parseEchoWireLinkPreviews);
  if (!linkPreviews.ok) return linkPreviews;
  const flags = readOptional(object.value, 'flags', path, parseStringArray);
  if (!flags.ok) return flags;
  const containsOnlyAttachments = readRequired(object.value, 'contains_only_attachments', path, parseBoolean);
  if (!containsOnlyAttachments.ok) return containsOnlyAttachments;
  const containsOnlyEntities = readRequired(object.value, 'contains_only_entities', path, parseBoolean);
  if (!containsOnlyEntities.ok) return containsOnlyEntities;
  const containsOnlyEmojiEntities = readRequired(object.value, 'contains_only_emoji_entities', path, parseBoolean);
  if (!containsOnlyEmojiEntities.ok) return containsOnlyEmojiEntities;
  const type = readOptional(object.value, 'type', path, parseEchoMessageType);
  if (!type.ok) return type;
  const data = readOptionalNullable(object.value, 'data', path, parseRecord);
  if (!data.ok) return data;
  const actors = readOptionalNullable(object.value, 'actors', path, parseRecordArray);
  if (!actors.ok) return actors;
  const sourceWidgetId = readOptionalNullable(object.value, 'source_widget_id', path, parseString);
  if (!sourceWidgetId.ok) return sourceWidgetId;
  const sourceWidget = readOptionalNullable(object.value, 'source_widget', path, parseRecord);
  if (!sourceWidget.ok) return sourceWidget;
  const clientRequestId = readOptional(object.value, 'optimistic_sort_order', path, parseClientRequestId);
  if (!clientRequestId.ok) return clientRequestId;
  const replyToMessageId = readOptional(object.value, 'reply_to_message_id', path, parseMessageId);
  if (!replyToMessageId.ok) return replyToMessageId;

  return ok({
    id: id.value,
    sortOrder: sortOrder.value,
    workspaceId: workspaceId.value,
    channelId: channelId.value,
    authorId: rawAuthorId.value ?? fallbackAuthorId(id.value, authorType.value),
    rawAuthorId: rawAuthorId.value,
    authorType: authorType.value,
    createdAt: createdAt.value,
    rendererRow: cloneEchoRendererRow(object.value),
    body: textFromSanitizedHtml(normalizedText),
    bodyHtml: normalizedText,
    containsOnlyAttachments: containsOnlyAttachments.value,
    containsOnlyEntities: containsOnlyEntities.value,
    containsOnlyEmojiEntities: containsOnlyEmojiEntities.value,
    attachments: attachments.value,
    previews: linkPreviews.value.map(chatPreviewFromWire),
    reactions: reactions.value,
    mentions: mentions.value,
    linkPreviews: linkPreviews.value,
    ...(flags.value === undefined ? {} : { flags: flags.value }),
    ...(type.value === undefined ? {} : { type: type.value }),
    ...(data.value == null ? {} : { data: data.value }),
    ...(actors.value == null ? {} : { actors: actors.value }),
    ...(sourceWidgetId.value == null ? {} : { sourceWidgetId: sourceWidgetId.value }),
    ...(sourceWidget.value == null ? {} : { sourceWidget: sourceWidget.value }),
    ...(clientRequestId.value === undefined ? {} : { clientRequestId: clientRequestId.value }),
    ...(replyToMessageId.value === undefined ? {} : { replyToMessageId: replyToMessageId.value }),
  });
}

/** Parses a feed-page success envelope or returns a path-annotated parse failure. */
export function parseEchoFeedPageResponse(value: unknown, path = '$', fallbackChannelId?: ChannelId): FeedParseResult<EchoFeedPage> {
  return parseSuccessEnvelope(value, path, (data, dataPath) => parseEchoFeedPageData(data, dataPath, fallbackChannelId));
}

/** Parses a sync success envelope and validates create/update vs destroy message presence. */
export function parseEchoSyncResponse(value: unknown, path = '$'): FeedParseResult<EchoWireSyncResponse> {
  return parseSuccessEnvelope(value, path, parseEchoSyncData);
}

/** Parses a send success envelope containing the authoritative echo wire message. */
export function parseEchoSendResponse(value: unknown, path = '$'): FeedParseResult<EchoSendResponse> {
  return parseSuccessEnvelope(value, path, parseEchoSendData);
}

/** Parses live message details, accepting either data.message or legacy data-as-message shapes. */
export function parseEchoSocketMessageEventDetail(value: unknown, path = '$'): FeedParseResult<EchoSocketMessageEventDetail> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const action = readRequired(object.value, 'action', path, parseEchoWireAction);
  if (!action.ok) return action;
  const cursor = readOptionalNullable(object.value, 'cursor', path, parseString);
  if (!cursor.ok) return cursor;
  const data = readRequired(object.value, 'data', path, parseRecord);
  if (!data.ok) return data;
  const nestedMessage = readOptional(data.value, 'message', joinPath(path, 'data'), parseEchoWireMessage);
  if (!nestedMessage.ok) return nestedMessage;

  if (nestedMessage.value !== undefined) {
    return ok({
      action: action.value,
      ...(cursor.value === undefined || cursor.value === null ? {} : { cursor: cursor.value }),
      message: nestedMessage.value,
    });
  }

  const message = parseEchoWireMessage(data.value, joinPath(path, 'data'));
  if (!message.ok) return message;

  return ok({
    action: action.value,
    ...(cursor.value === undefined || cursor.value === null ? {} : { cursor: cursor.value }),
    message: message.value,
  });
}

/** Parses live reaction details with the mutation action and nested reaction payload. */
export function parseEchoSocketReactionEventDetail(value: unknown, path = '$'): FeedParseResult<EchoSocketReactionEventDetail> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const action = readRequired(object.value, 'action', path, parseEchoWireAction);
  if (!action.ok) return action;
  const reaction = readRequired(object.value, 'data', path, parseEchoWireReaction);
  if (!reaction.ok) return reaction;

  return ok({ action: action.value, reaction: reaction.value });
}

/** Parses local optimistic message details, accepting direct data rows and data.message rows. */
export function parseEchoLocalOptimisticMessageEventDetail(
  value: unknown,
  path = '$',
): FeedParseResult<EchoLocalOptimisticMessageEventDetail> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const action = readRequired(object.value, 'action', path, parseEchoWireAction);
  if (!action.ok) return action;
  if (action.value !== 'create') return invalid(joinPath(path, 'action'), 'expected optimistic create action');

  const data = readRequired(object.value, 'data', path, parseRecord);
  if (!data.ok) return data;
  const rowPath = isRecord(data.value.message) ? joinPath(joinPath(path, 'data'), 'message') : joinPath(path, 'data');
  const row = isRecord(data.value.message) ? data.value.message : data.value;

  const workspaceId = readRequired(row, 'workspace_id', rowPath, parseWorkspaceId);
  if (!workspaceId.ok) return workspaceId;
  const channelId = readRequired(row, 'channel_id', rowPath, parseChannelId);
  if (!channelId.ok) return channelId;
  const authorId = readRequired(row, 'author_id', rowPath, parseAuthorId);
  if (!authorId.ok) return authorId;
  const clientRequestId = readRequired(row, 'optimistic_sort_order', rowPath, parseClientRequestId);
  if (!clientRequestId.ok) return clientRequestId;
  const text = readRequired(row, 'text', rowPath, parseString);
  if (!text.ok) return text;

  return ok({
    action: 'create',
    message: {
      workspaceId: workspaceId.value,
      channelId: channelId.value,
      authorId: authorId.value,
      clientRequestId: clientRequestId.value,
      body: text.value,
      rendererRow: cloneEchoRendererRow(row),
    },
  });
}

/** Parses non-success echo envelopes; success or malformed envelopes return parse failures. */
export function parseEchoErrorEnvelope(value: unknown, path = '$'): FeedParseResult<EchoErrorEnvelope> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const okFlag = readRequired(object.value, 'ok', path, parseBoolean);
  if (!okFlag.ok) return okFlag;
  if (okFlag.value) return invalid(joinPath(path, 'ok'), 'expected error envelope');
  const errors = readRequired(object.value, 'errors', path, parseStringArray);
  if (!errors.ok) return errors;
  const validations = readOptional(object.value, 'validations', path, parseValidationMessages);
  if (!validations.ok) return validations;

  return ok({
    ok: false,
    errors: errors.value,
    ...(validations.value === undefined ? {} : { validations: validations.value }),
  });
}

function parseSuccessEnvelope<T>(value: unknown, path: string, parseData: FeedJsonParser<T>): FeedParseResult<T> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const okFlag = readRequired(object.value, 'ok', path, parseBoolean);
  if (!okFlag.ok) return okFlag;
  if (!okFlag.value) return invalid(joinPath(path, 'ok'), 'expected success envelope');
  return readRequired(object.value, 'data', path, parseData);
}

function parseEchoFeedPageData(value: unknown, path = '$', fallbackChannelId?: ChannelId): FeedParseResult<EchoFeedPage> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const messages = readRequired(object.value, 'messages', path, (messageValue, messagePath) =>
    parseEchoFeedPageMessages(messageValue, messagePath, fallbackChannelId),
  );
  if (!messages.ok) return messages;
  const rendererProfiles = readRequired(object.value, 'entities', path, parseEchoRendererEntities);
  if (!rendererProfiles.ok) return rendererProfiles;
  const olderCursor = readRequiredNullable(object.value, 'older_cursor', path, parseSortOrder);
  if (!olderCursor.ok) return olderCursor;
  const newerCursor = readRequiredNullable(object.value, 'newer_cursor', path, parseSortOrder);
  if (!newerCursor.ok) return newerCursor;
  const syncCursor = readRequired(object.value, 'snapshot_cursor', path, parseString);
  if (!syncCursor.ok) return syncCursor;
  const resolvedId = readRequiredNullable(object.value, 'resolved_id', path, parseMessageId);
  if (!resolvedId.ok) return resolvedId;
  const resolvedSortOrder = readRequiredNullable(object.value, 'resolved_sort_order', path, parseSortOrder);
  if (!resolvedSortOrder.ok) return resolvedSortOrder;

  return ok({
    messages: decorateMessagesWithRendererProfiles(messages.value.messages, rendererProfiles.value),
    entities: emptyEntityBundle(),
    rendererProfiles: rendererProfiles.value,
    ...(olderCursor.value === null ? {} : { olderCursor: olderCursor.value }),
    ...(newerCursor.value === null ? {} : { newerCursor: newerCursor.value }),
    ...(resolvedId.value === null ? {} : { resolvedId: resolvedId.value }),
    ...(resolvedSortOrder.value === null ? {} : { resolvedSortOrder: resolvedSortOrder.value }),
    ...(messages.value.invalidMessages.length === 0 ? {} : { invalidMessages: messages.value.invalidMessages }),
    syncCursor: syncCursor.value,
  });
}

function parseEchoSyncData(value: unknown, path = '$'): FeedParseResult<EchoWireSyncResponse> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const events = readRequired(object.value, 'events', path, parseEchoWireSyncEvents);
  if (!events.ok) return events;
  const rendererProfiles = readRequired(object.value, 'entities', path, parseEchoRendererEntities);
  if (!rendererProfiles.ok) return rendererProfiles;
  const overflow = readRequired(object.value, 'overflow', path, parseBoolean);
  if (!overflow.ok) return overflow;
  const nextCursor = readRequired(object.value, 'next_cursor', path, parseString);
  if (!nextCursor.ok) return nextCursor;

  return ok({
    events: decorateSyncEventsWithRendererProfiles(events.value, rendererProfiles.value),
    rendererProfiles: rendererProfiles.value,
    overflow: overflow.value,
    nextCursor: nextCursor.value,
  });
}

function parseEchoSendData(value: unknown, path = '$'): FeedParseResult<EchoSendResponse> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const message = readRequired(object.value, 'message', path, parseEchoWireMessage);
  if (!message.ok) return message;
  return ok({ message: message.value });
}

function parseEchoWireSyncEvent(value: unknown, path = '$'): FeedParseResult<EchoWireSyncEvent> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const cursor = readRequired(object.value, 'cursor', path, parseString);
  if (!cursor.ok) return cursor;
  const action = readRequired(object.value, 'action', path, parseEchoWireAction);
  if (!action.ok) return action;
  const sortOrder = readRequired(object.value, 'sort_order', path, parseSortOrder);
  if (!sortOrder.ok) return sortOrder;
  const message = readRequiredNullable(object.value, 'message', path, parseEchoWireMessage);
  if (!message.ok) return message;

  if (action.value === 'destroy') {
    if (message.value !== null) {
      return invalid(joinPath(path, 'message'), 'destroy sync events must carry null message');
    }
    return ok({
      cursor: cursor.value,
      action: action.value,
      sortOrder: sortOrder.value,
      message: message.value,
    });
  }
  if (message.value === null) {
    return invalid(joinPath(path, 'message'), 'create/update sync events must carry a message');
  }

  return ok({
    cursor: cursor.value,
    action: action.value,
    sortOrder: sortOrder.value,
    message: message.value,
  });
}

function parseEchoRendererEntities(value: unknown, path = '$'): FeedParseResult<EchoRendererProfile[]> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;
  return readRequired(object.value, 'profiles', path, (profiles, profilesPath) =>
    parseArray(profiles, profilesPath ?? joinPath(path, 'profiles'), parseEchoRendererProfile),
  );
}

function parseEchoRendererProfile(value: unknown, path = '$'): FeedParseResult<EchoRendererProfile> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;
  const id = readRequired(object.value, 'id', path, parseAuthorId);
  if (!id.ok) return id;
  const workspaceId = readRequired(object.value, 'workspace_id', path, parseWorkspaceId);
  if (!workspaceId.ok) return workspaceId;
  const fullName = readRequired(object.value, 'full_name', path, parseString);
  if (!fullName.ok) return fullName;
  const flexicon = readRequired(object.value, 'flexicon', path, parseRecord);
  if (!flexicon.ok) return flexicon;
  const role = readRequired(object.value, 'role', path, parseString);
  if (!role.ok) return role;
  const tag = readRequiredNullable(object.value, 'tag', path, parseString);
  if (!tag.ok) return tag;
  const tagColor = readRequiredNullable(object.value, 'tag_color', path, parseString);
  if (!tagColor.ok) return tagColor;

  return ok({
    id: id.value,
    workspace_id: workspaceId.value,
    full_name: fullName.value,
    flexicon: flexicon.value,
    role: role.value,
    tag: tag.value,
    tag_color: tagColor.value,
  });
}

function decorateMessagesWithRendererProfiles(
  messages: readonly EchoWireMessage[],
  profiles: readonly EchoRendererProfile[],
): EchoWireMessage[] {
  const profilesByKey = new Map(profiles.map((profile) => [workspaceEntityKey(profile.workspace_id, profile.id), profile]));
  return messages.map((message) => decorateMessageWithRendererProfiles(message, profilesByKey));
}

function decorateMessageWithRendererProfiles(
  message: EchoWireMessage,
  profilesByKey: ReadonlyMap<string, EchoRendererProfile>,
): EchoWireMessage {
  const author =
    message.authorType === 'profile' && message.rawAuthorId !== null
      ? buildProfileSnapshot(profilesByKey.get(workspaceEntityKey(message.workspaceId, message.rawAuthorId)))
      : null;
  // Announcement sentences resolve their actors from `data` hashids; seed those
  // snapshots so a fresh cache renders them on the page's own first paint.
  const actorSnapshots: Record<string, Record<string, unknown>> = {};
  for (const profileId of entityTargetsFromMessageData(message.data).profile) {
    const snapshot = buildProfileSnapshot(profilesByKey.get(workspaceEntityKey(message.workspaceId, profileId)));
    if (snapshot) actorSnapshots[profileId] = snapshot;
  }
  if (!author && Object.keys(actorSnapshots).length === 0) return message;

  const rendererRow = isRecord(message.rendererRow) ? message.rendererRow : {};
  const existingSnapshots = isRecord(rendererRow.entity_snapshots) ? rendererRow.entity_snapshots : undefined;
  const existingProfiles = existingSnapshots && isRecord(existingSnapshots.profile) ? existingSnapshots.profile : undefined;
  return {
    ...message,
    rendererRow: {
      ...rendererRow,
      ...(author ? { author } : {}),
      ...(Object.keys(actorSnapshots).length === 0
        ? {}
        : { entity_snapshots: { ...existingSnapshots, profile: { ...existingProfiles, ...actorSnapshots } } }),
    },
  };
}

function decorateSyncEventsWithRendererProfiles(
  events: readonly EchoWireSyncEvent[],
  profiles: readonly EchoRendererProfile[],
): EchoWireSyncEvent[] {
  const messages = decorateMessagesWithRendererProfiles(
    events.flatMap((event) => (event.message ? [event.message] : [])),
    profiles,
  );
  let messageIndex = 0;
  return events.map((event) => {
    if (!event.message) return event;
    const message = messages[messageIndex];
    messageIndex += 1;
    return { ...event, message: message! };
  });
}

function workspaceEntityKey(workspaceId: WorkspaceId, entityId: string): string {
  return `${workspaceId}\x1f${entityId}`;
}

function parseEchoWireAttachment(value: unknown, path = '$'): FeedParseResult<EchoWireAttachment> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const id = readRequired(object.value, 'id', path, parseString);
  if (!id.ok) return id;
  const workspaceId = readOptional(object.value, 'workspace_id', path, parseWorkspaceId);
  if (!workspaceId.ok) return workspaceId;
  const metadata = readRequired(object.value, 'metadata', path, parseAttachmentMetadata);
  if (!metadata.ok) return metadata;
  const publicUrl = readRequired(object.value, 'public_url', path, parseString);
  if (!publicUrl.ok) return publicUrl;
  const variants = readOptional(object.value, 'variants', path, parseAttachmentVariants);
  if (!variants.ok) return variants;
  const createdAt = readRequired(object.value, 'created_at', path, parseNumber);
  if (!createdAt.ok) return createdAt;
  const updatedAt = readRequired(object.value, 'updated_at', path, parseNumber);
  if (!updatedAt.ok) return updatedAt;

  return ok({
    id: id.value,
    ...(workspaceId.value === undefined ? {} : { workspaceId: workspaceId.value }),
    contentType: metadata.value.contentType,
    fileName: metadata.value.fileName,
    sizeBytes: metadata.value.sizeBytes,
    ...(metadata.value.thumbhash === undefined ? {} : { thumbhash: metadata.value.thumbhash }),
    originalUrl: publicUrl.value,
    ...(variants.value === undefined ? {} : { variants: variants.value }),
    ...(metadata.value.thumbWidth === undefined ? {} : { thumbWidth: metadata.value.thumbWidth }),
    ...(metadata.value.thumbHeight === undefined ? {} : { thumbHeight: metadata.value.thumbHeight }),
    ...(metadata.value.contentMd5 === undefined ? {} : { contentMd5: metadata.value.contentMd5 }),
    createdAt: createdAt.value,
    updatedAt: updatedAt.value,
  });
}

function parseEchoWireReaction(value: unknown, path = '$'): FeedParseResult<EchoWireReaction> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const id = readRequired(object.value, 'id', path, parseString);
  if (!id.ok) return id;
  const workspaceId = readRequired(object.value, 'workspace_id', path, parseWorkspaceId);
  if (!workspaceId.ok) return workspaceId;
  const channelId = readRequired(object.value, 'channel_id', path, parseChannelId);
  if (!channelId.ok) return channelId;
  const messageId = readRequired(object.value, 'message_id', path, parseMessageId);
  if (!messageId.ok) return messageId;
  const profileId = readRequired(object.value, 'profile_id', path, parseAuthorId);
  if (!profileId.ok) return profileId;
  const emojiId = readRequired(object.value, 'emoji_id', path, parseEmojiId);
  if (!emojiId.ok) return emojiId;
  const sortOrder = readRequired(object.value, 'sort_order', path, parseSortOrder);
  if (!sortOrder.ok) return sortOrder;
  const posX = readRequiredNullable(object.value, 'pos_x', path, parseNumber);
  if (!posX.ok) return posX;
  const posY = readRequiredNullable(object.value, 'pos_y', path, parseNumber);
  if (!posY.ok) return posY;
  const scale = readRequiredNullable(object.value, 'scale', path, parseNumber);
  if (!scale.ok) return scale;
  const angle = readRequiredNullable(object.value, 'angle', path, parseNumber);
  if (!angle.ok) return angle;
  const clientRequestId = readOptional(object.value, 'optimistic_sort_order', path, parseClientRequestId);
  if (!clientRequestId.ok) return clientRequestId;

  return ok({
    id: id.value,
    workspaceId: workspaceId.value,
    channelId: channelId.value,
    messageId: messageId.value,
    profileId: profileId.value,
    emojiId: emojiId.value,
    sortOrder: sortOrder.value,
    posX: posX.value,
    posY: posY.value,
    scale: scale.value,
    angle: angle.value,
    ...(clientRequestId.value === undefined ? {} : { clientRequestId: clientRequestId.value }),
  });
}

function parseEchoWireMention(value: unknown, path = '$'): FeedParseResult<EchoWireMention> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const id = readRequired(object.value, 'id', path, parseString);
  if (!id.ok) return id;
  const workspaceId = readRequired(object.value, 'workspace_id', path, parseWorkspaceId);
  if (!workspaceId.ok) return workspaceId;
  const timestamp = readRequired(object.value, 'timestamp', path, parseNumber);
  if (!timestamp.ok) return timestamp;
  const senderId = readRequired(object.value, 'sender_id', path, parseAuthorId);
  if (!senderId.ok) return senderId;
  const recipientId = readRequired(object.value, 'recipient_id', path, parseAuthorId);
  if (!recipientId.ok) return recipientId;
  const sourceType = readOptional(object.value, 'source_type', path, parseString);
  if (!sourceType.ok) return sourceType;
  const sourceId = readOptional(object.value, 'source_id', path, parseString);
  if (!sourceId.ok) return sourceId;
  const mentionableType = readOptional(object.value, 'mentionable_type', path, parseString);
  if (!mentionableType.ok) return mentionableType;
  const mentionableId = readOptional(object.value, 'mentionable_id', path, parseString);
  if (!mentionableId.ok) return mentionableId;
  const emailSubject = readOptional(object.value, 'email_subject', path, parseString);
  if (!emailSubject.ok) return emailSubject;
  const notificationTitle = readOptional(object.value, 'notification_title', path, parseString);
  if (!notificationTitle.ok) return notificationTitle;
  const summary = readOptional(object.value, 'summary', path, parseString);
  if (!summary.ok) return summary;

  return ok({
    id: id.value,
    workspaceId: workspaceId.value,
    timestamp: timestamp.value,
    senderId: senderId.value,
    recipientId: recipientId.value,
    ...(sourceType.value === undefined ? {} : { sourceType: sourceType.value }),
    ...(sourceId.value === undefined ? {} : { sourceId: sourceId.value }),
    ...(mentionableType.value === undefined ? {} : { mentionableType: mentionableType.value }),
    ...(mentionableId.value === undefined ? {} : { mentionableId: mentionableId.value }),
    ...(emailSubject.value === undefined ? {} : { emailSubject: emailSubject.value }),
    ...(notificationTitle.value === undefined ? {} : { notificationTitle: notificationTitle.value }),
    ...(summary.value === undefined ? {} : { summary: summary.value }),
  });
}

function parseEchoWireLinkPreview(value: unknown, path = '$'): FeedParseResult<EchoWireLinkPreview> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const id = readRequired(object.value, 'id', path, parseString);
  if (!id.ok) return id;
  const dismissed = readRequired(object.value, 'dismissed', path, parseBoolean);
  if (!dismissed.ok) return dismissed;
  const dismissedAt = readOptionalNullable(object.value, 'dismissed_at', path, parseString);
  if (!dismissedAt.ok) return dismissedAt;
  const url = readRequired(object.value, 'url', path, parseString);
  if (!url.ok) return url;
  const title = readOptionalNullable(object.value, 'title', path, parseString);
  if (!title.ok) return title;
  const description = readOptionalNullable(object.value, 'description', path, parseString);
  if (!description.ok) return description;
  const imageUrl = readOptionalNullable(object.value, 'image_url', path, parseString);
  if (!imageUrl.ok) return imageUrl;
  const imageWidth = readOptionalNullable(object.value, 'image_width', path, parseNumber);
  if (!imageWidth.ok) return imageWidth;
  const imageHeight = readOptionalNullable(object.value, 'image_height', path, parseNumber);
  if (!imageHeight.ok) return imageHeight;
  const logoUrl = readOptionalNullable(object.value, 'logo_url', path, parseString);
  if (!logoUrl.ok) return logoUrl;
  const logoWidth = readOptionalNullable(object.value, 'logo_width', path, parseNumber);
  if (!logoWidth.ok) return logoWidth;
  const logoHeight = readOptionalNullable(object.value, 'logo_height', path, parseNumber);
  if (!logoHeight.ok) return logoHeight;
  const publisher = readOptionalNullable(object.value, 'publisher', path, parseString);
  if (!publisher.ok) return publisher;
  const author = readOptionalNullable(object.value, 'author', path, parseString);
  if (!author.ok) return author;
  const lang = readOptionalNullable(object.value, 'lang', path, parseString);
  if (!lang.ok) return lang;
  const canonicalUrl = readOptionalNullable(object.value, 'canonical_url', path, parseString);
  if (!canonicalUrl.ok) return canonicalUrl;
  const date = readOptionalNullable(object.value, 'date', path, parseNumber);
  if (!date.ok) return date;
  const datePublished = readOptionalNullable(object.value, 'date_published', path, parseNumber);
  if (!datePublished.ok) return datePublished;
  const dateModified = readOptionalNullable(object.value, 'date_modified', path, parseNumber);
  if (!dateModified.ok) return dateModified;

  return ok({
    id: id.value,
    dismissed: dismissed.value,
    ...(dismissedAt.value == null ? {} : { dismissedAt: dismissedAt.value }),
    url: url.value,
    ...(title.value == null ? {} : { title: title.value }),
    ...(description.value == null ? {} : { description: description.value }),
    ...(imageUrl.value == null ? {} : { imageUrl: imageUrl.value }),
    ...(imageWidth.value == null ? {} : { imageWidth: imageWidth.value }),
    ...(imageHeight.value == null ? {} : { imageHeight: imageHeight.value }),
    ...(logoUrl.value == null ? {} : { logoUrl: logoUrl.value }),
    ...(logoWidth.value == null ? {} : { logoWidth: logoWidth.value }),
    ...(logoHeight.value == null ? {} : { logoHeight: logoHeight.value }),
    ...(publisher.value == null ? {} : { publisher: publisher.value }),
    ...(author.value == null ? {} : { author: author.value }),
    ...(lang.value == null ? {} : { lang: lang.value }),
    ...(canonicalUrl.value == null ? {} : { canonicalUrl: canonicalUrl.value }),
    ...(date.value == null ? {} : { date: date.value }),
    ...(datePublished.value == null ? {} : { datePublished: datePublished.value }),
    ...(dateModified.value == null ? {} : { dateModified: dateModified.value }),
  });
}

interface ParsedAttachmentMetadata {
  readonly fileName: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly thumbhash?: string;
  readonly thumbWidth?: number;
  readonly thumbHeight?: number;
  readonly contentMd5?: string;
}

function parseAttachmentMetadata(value: unknown, path = '$'): FeedParseResult<ParsedAttachmentMetadata> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const fileName = readRequired(object.value, 'file_name', path, parseString);
  if (!fileName.ok) return fileName;
  const contentType = readRequired(object.value, 'content_type', path, parseString);
  if (!contentType.ok) return contentType;
  const contentLength = readRequired(object.value, 'content_length', path, parseNonNegativeInteger);
  if (!contentLength.ok) return contentLength;
  const thumbhash = readOptional(object.value, 'thumbhash', path, parseString);
  if (!thumbhash.ok) return thumbhash;
  const thumbsize = readOptional(object.value, 'thumbsize', path, parseThumbsize);
  if (!thumbsize.ok) return thumbsize;
  const contentMd5 = readOptional(object.value, 'content_md5', path, parseString);
  if (!contentMd5.ok) return contentMd5;

  return ok({
    fileName: fileName.value,
    contentType: contentType.value,
    sizeBytes: contentLength.value,
    ...(thumbhash.value === undefined ? {} : { thumbhash: thumbhash.value }),
    ...(thumbsize.value === undefined ? {} : { thumbWidth: thumbsize.value.width, thumbHeight: thumbsize.value.height }),
    ...(contentMd5.value === undefined ? {} : { contentMd5: contentMd5.value }),
  });
}

function parseAttachmentVariants(value: unknown, path = '$'): FeedParseResult<EchoWireAttachment['variants']> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const medium = readOptional(object.value, 'medium', path, parseBackendMediaVariant);
  if (!medium.ok) return medium;
  const large = readOptional(object.value, 'large', path, parseBackendMediaVariant);
  if (!large.ok) return large;

  return ok({
    ...(medium.value === undefined ? {} : { medium: medium.value }),
    ...(large.value === undefined ? {} : { large: large.value }),
  });
}

function parseBackendMediaVariant(value: unknown, path = '$'): FeedParseResult<WireMediaVariant> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const width = readRequired(object.value, 'width', path, parsePositiveInteger);
  if (!width.ok) return width;
  const height = readRequired(object.value, 'height', path, parsePositiveInteger);
  if (!height.ok) return height;
  const publicUrl = readRequired(object.value, 'public_url', path, parseString);
  if (!publicUrl.ok) return publicUrl;

  return ok({
    width: width.value,
    height: height.value,
    url: publicUrl.value,
  });
}

function chatPreviewFromWire(preview: EchoWireLinkPreview): EchoChatPreview {
  const image = chatPreviewImageFromWire(preview);
  return {
    id: preview.id,
    dismissed: preview.dismissed,
    ...(preview.dismissedAt === undefined ? {} : { dismissedAt: preview.dismissedAt }),
    url: preview.url,
    title: preview.title ?? preview.url,
    description: preview.description ?? '',
    ...(image === undefined ? {} : { image }),
    ...(preview.imageUrl === undefined ? {} : { imageUrl: preview.imageUrl }),
    ...(preview.imageWidth === undefined ? {} : { imageWidth: preview.imageWidth }),
    ...(preview.imageHeight === undefined ? {} : { imageHeight: preview.imageHeight }),
    ...(preview.logoUrl === undefined ? {} : { logoUrl: preview.logoUrl }),
    ...(preview.logoWidth === undefined ? {} : { logoWidth: preview.logoWidth }),
    ...(preview.logoHeight === undefined ? {} : { logoHeight: preview.logoHeight }),
    ...(preview.publisher === undefined ? {} : { publisher: preview.publisher }),
    ...(preview.author === undefined ? {} : { author: preview.author }),
    ...(preview.lang === undefined ? {} : { lang: preview.lang }),
    ...(preview.canonicalUrl === undefined ? {} : { canonicalUrl: preview.canonicalUrl }),
    ...(preview.date === undefined ? {} : { date: preview.date }),
    ...(preview.datePublished === undefined ? {} : { datePublished: preview.datePublished }),
    ...(preview.dateModified === undefined ? {} : { dateModified: preview.dateModified }),
  };
}

function chatPreviewImageFromWire(preview: EchoWireLinkPreview): ChatPreviewImage | undefined {
  if (preview.imageUrl === undefined || preview.imageWidth === undefined || preview.imageHeight === undefined) {
    return undefined;
  }

  return {
    url: preview.imageUrl,
    width: preview.imageWidth,
    height: preview.imageHeight,
    thumbhash: '',
  };
}

function parseEchoFeedPageMessages(
  value: unknown,
  path = '$',
  fallbackChannelId?: ChannelId,
): FeedParseResult<{
  readonly messages: EchoWireMessage[];
  readonly invalidMessages: EchoInvalidMessageIssue[];
}> {
  if (!Array.isArray(value)) return expected(path, 'array', value);

  const messages: EchoWireMessage[] = [];
  const invalidMessages: EchoInvalidMessageIssue[] = [];
  for (let index = 0; index < value.length; index += 1) {
    const itemPath = `${path}[${index}]`;
    const parsed = parseEchoWireMessage(value[index], itemPath);
    if (parsed.ok) {
      messages.push(parsed.value);
      continue;
    }

    const placeholder = invalidEchoMessagePlaceholder(value[index], itemPath, fallbackChannelId);
    invalidMessages.push({
      path: parsed.error.path,
      message: parsed.error.message,
      disposition: placeholder ? 'placeholder' : 'skipped',
      ...(placeholder ? { id: placeholder.id, sortOrder: placeholder.sortOrder } : {}),
    });
    if (placeholder) messages.push(placeholder);
  }

  return ok({ messages, invalidMessages });
}

function invalidEchoMessagePlaceholder(value: unknown, path: string, fallbackChannelId?: ChannelId): EchoWireMessage | undefined {
  const object = parseRecord(value, path);
  if (!object.ok) return undefined;
  const id = readRequired(object.value, 'id', path, parseMessageId);
  if (!id.ok) return undefined;
  const sortOrder = readRequired(object.value, 'sort_order', path, parseSortOrder);
  if (!sortOrder.ok) return undefined;

  const workspaceId = readRequired(object.value, 'workspace_id', path, parseWorkspaceId);
  if (!workspaceId.ok) return undefined;
  const rawChannelId = object.value.channel_id;
  const channelId = fallbackChannelId ?? (typeof rawChannelId === 'string' && rawChannelId.length > 0 ? rawChannelId : undefined);
  if (channelId === undefined) return undefined;

  const timestamp = parseUnixSecondsToIso(object.value.timestamp, joinPath(path, 'timestamp'));
  const createdAt = timestamp.ok ? timestamp.value : new Date(decodeTime(sortOrder.value)).toISOString();

  return {
    id: id.value,
    sortOrder: sortOrder.value,
    workspaceId: workspaceId.value,
    channelId,
    authorId: fallbackAuthorId(id.value, null),
    rawAuthorId: null,
    authorType: null,
    createdAt,
    body: '',
    bodyHtml: '',
    flags: [ECHO_INVALID_MESSAGE_FLAG],
    containsOnlyAttachments: false,
    containsOnlyEntities: false,
    containsOnlyEmojiEntities: false,
    attachments: [],
    previews: [],
    reactions: [],
    mentions: [],
    linkPreviews: [],
  };
}

function parseEchoWireAttachments(value: unknown, path = '$'): FeedParseResult<EchoWireAttachment[]> {
  return parseArray(value, path, parseEchoWireAttachment);
}

function parseEchoWireReactions(value: unknown, path = '$'): FeedParseResult<EchoWireReaction[]> {
  return parseArray(value, path, parseEchoWireReaction);
}

function parseEchoWireMentions(value: unknown, path = '$'): FeedParseResult<EchoWireMention[]> {
  return parseArray(value, path, parseEchoWireMention);
}

function parseEchoWireLinkPreviews(value: unknown, path = '$'): FeedParseResult<EchoWireLinkPreview[]> {
  return parseArray(value, path, parseEchoWireLinkPreview);
}

function parseEchoWireSyncEvents(value: unknown, path = '$'): FeedParseResult<EchoWireSyncEvent[]> {
  return parseArray(value, path, parseEchoWireSyncEvent);
}

function parseStringArray(value: unknown, path = '$'): FeedParseResult<string[]> {
  return parseArray(value, path, parseString);
}

function parseRecordArray(value: unknown, path = '$'): FeedParseResult<Record<string, unknown>[]> {
  return parseArray(value, path, parseRecord);
}

function parseValidationMessages(value: unknown, path = '$'): FeedParseResult<Record<string, string[]>> {
  const object = parseRecord(value, path);
  if (!object.ok) return object;

  const validations: Record<string, string[]> = {};
  for (const [key, rawMessages] of Object.entries(object.value)) {
    const messages = parseStringArray(rawMessages, joinPath(path, key));
    if (!messages.ok) return messages;
    validations[key] = messages.value;
  }
  return ok(validations);
}

function parseString(value: unknown, path = '$'): FeedParseResult<string> {
  if (typeof value !== 'string') return expected(path, 'string', value);
  return ok(value);
}

function parseMessageId(value: unknown, path = '$'): FeedParseResult<MessageId> {
  const string = parseString(value, path);
  if (!string.ok) return string;

  const messageId = messageIdFromString(string.value);
  if (!messageId) return expected(path, '8-character uppercase Crockford id', value);
  return ok(messageId);
}

function parseSortOrder(value: unknown, path = '$'): FeedParseResult<SortOrder> {
  const string = parseString(value, path);
  if (!string.ok) return string;

  const sortOrder = sortOrderFromString(string.value);
  if (!sortOrder) return expected(path, '26-character uppercase Crockford ULID', value);
  return ok(sortOrder);
}

function parseWorkspaceId(value: unknown, path = '$'): FeedParseResult<WorkspaceId> {
  const string = parseString(value, path);
  return string.ok ? ok(string.value) : string;
}

function parseChannelId(value: unknown, path = '$'): FeedParseResult<ChannelId> {
  const string = parseString(value, path);
  return string.ok ? ok(string.value) : string;
}

function parseAuthorId(value: unknown, path = '$'): FeedParseResult<AuthorId> {
  const string = parseString(value, path);
  return string.ok ? ok(string.value) : string;
}

function parseEmojiId(value: unknown, path = '$'): FeedParseResult<EmojiId> {
  const string = parseString(value, path);
  return string.ok ? ok(string.value) : string;
}

function parseClientRequestId(value: unknown, path = '$'): FeedParseResult<ClientRequestId> {
  const string = parseString(value, path);
  return string.ok ? ok(string.value) : string;
}

function parseNumber(value: unknown, path = '$'): FeedParseResult<number> {
  if (typeof value !== 'number' || !Number.isFinite(value)) return expected(path, 'finite number', value);
  return ok(value);
}

function parsePositiveInteger(value: unknown, path = '$'): FeedParseResult<number> {
  const number = parseNumber(value, path);
  if (!number.ok) return number;
  if (!Number.isInteger(number.value) || number.value <= 0) return invalid(path, 'expected positive integer');
  return number;
}

function parseNonNegativeInteger(value: unknown, path = '$'): FeedParseResult<number> {
  const number = parseNumber(value, path);
  if (!number.ok) return number;
  if (!Number.isInteger(number.value) || number.value < 0) return invalid(path, 'expected non-negative integer');
  return number;
}

function parseUnixSecondsToIso(value: unknown, path = '$'): FeedParseResult<string> {
  const seconds = parseNonNegativeInteger(value, path);
  if (!seconds.ok) return seconds;

  const date = new Date(seconds.value * 1000);
  if (!Number.isFinite(date.getTime())) return invalid(path, 'unix timestamp seconds are out of range');
  return ok(date.toISOString());
}

function parseBoolean(value: unknown, path = '$'): FeedParseResult<boolean> {
  if (typeof value !== 'boolean') return expected(path, 'boolean', value);
  return ok(value);
}

function parseEchoMessageType(value: unknown, path = '$'): FeedParseResult<EchoMessageType> {
  if (value === 'announcement' || value === 'conference_transcript' || value === 'widget_announcement' || value === 'end_of_messages') {
    return ok(value);
  }
  return expected(path, 'one of announcement, conference_transcript, widget_announcement, end_of_messages', value);
}

function parseAuthorType(value: unknown, path = '$'): FeedParseResult<EchoAuthorType> {
  if (value === 'profile' || value === 'shadow') return ok(value);
  return expected(path, 'one of profile, shadow', value);
}

function parseEchoWireAction(value: unknown, path = '$'): FeedParseResult<EchoWireAction> {
  if (value === 'create' || value === 'update' || value === 'destroy') return ok(value);
  return expected(path, 'one of create, update, destroy', value);
}

function parseThumbsize(value: unknown, path = '$'): FeedParseResult<{ readonly width: number; readonly height: number }> {
  const string = parseString(value, path);
  if (!string.ok) return string;

  const match = /^([1-9][0-9]*)x([1-9][0-9]*)$/.exec(string.value);
  if (!match) return invalid(path, 'expected thumbsize formatted as WxH');
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height)) {
    return invalid(path, 'thumbsize dimensions are out of range');
  }
  return ok({ width, height });
}

function parseArray<T>(value: unknown, path: string, parseItem: FeedJsonParser<T>): FeedParseResult<T[]> {
  if (!Array.isArray(value)) return expected(path, 'array', value);

  const parsed: T[] = [];
  for (let index = 0; index < value.length; index += 1) {
    const item = parseItem(value[index], `${path}[${index}]`);
    if (!item.ok) return item;
    parsed.push(item.value);
  }
  return ok(parsed);
}

function parseRecord(value: unknown, path = '$'): FeedParseResult<Record<string, unknown>> {
  if (!isRecord(value)) return expected(path, 'object', value);
  return ok(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readRequired<T>(object: Record<string, unknown>, key: string, path: string, parseValue: FeedJsonParser<T>): FeedParseResult<T> {
  if (!Object.hasOwn(object, key)) return expected(joinPath(path, key), 'present value', undefined);
  return parseValue(object[key], joinPath(path, key));
}

function readRequiredNullable<T>(
  object: Record<string, unknown>,
  key: string,
  path: string,
  parseValue: FeedJsonParser<T>,
): FeedParseResult<T | null> {
  if (!Object.hasOwn(object, key)) return expected(joinPath(path, key), 'present value', undefined);
  if (object[key] === null) return ok(null);
  return parseValue(object[key], joinPath(path, key));
}

// The echo serializer emits explicit `null` for absent optional values (as_json of a
// nil column); readers must treat null exactly like an absent key.
function readOptional<T>(
  object: Record<string, unknown>,
  key: string,
  path: string,
  parseValue: FeedJsonParser<T>,
): FeedParseResult<T | undefined> {
  if (!Object.hasOwn(object, key) || object[key] == null) return ok(undefined);
  return parseValue(object[key], joinPath(path, key));
}

function readOptionalNullable<T>(
  object: Record<string, unknown>,
  key: string,
  path: string,
  parseValue: FeedJsonParser<T>,
): FeedParseResult<T | null | undefined> {
  if (!Object.hasOwn(object, key) || object[key] === undefined) return ok(undefined);
  if (object[key] === null) return ok(null);
  return parseValue(object[key], joinPath(path, key));
}

function ok<T>(value: T): FeedParseResult<T> {
  return { ok: true, value };
}

function expected(path: string, expectedType: string, value: unknown): FeedParseResult<never> {
  return {
    ok: false,
    error: {
      path,
      message: `expected ${expectedType}, received ${describeValue(value)}`,
    },
  };
}

function invalid(path: string, message: string): FeedParseResult<never> {
  return { ok: false, error: { path, message } };
}

function joinPath(path: string, key: string): string {
  return `${path}.${key}`;
}

function describeValue(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

function fallbackAuthorId(messageId: MessageId, authorType: EchoAuthorType | null): AuthorId {
  return `missing:${authorType ?? 'author'}:${messageId}`;
}

function emptyEntityBundle(): EntityBundle {
  return {
    profiles: [],
    channels: [],
    emoji: [],
  };
}

type FeedJsonParser<T> = (value: unknown, path?: string) => FeedParseResult<T>;
